/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { call, put, select } from 'redux-saga/effects';

import { showFlag } from 'src/redux/flags';
import { messages } from 'src/sections/repository/sections/branches/components/dialogs/bulk-delete-branches-dialog.i18n';
import { DetailedBranch } from 'src/sections/repository/sections/branches/types';
import urls from 'src/sections/repository/urls';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { Action } from 'src/types/state';
import { publishTrackEvent } from 'src/utils/analytics/publish';
import { authRequest, jsonHeaders } from 'src/utils/fetch';
import {
  captureExceptionWithTags,
  captureMessageForResponse,
} from 'src/utils/sentry';

import {
  onToggleBranchSelectSave,
  BULK_DELETE_BRANCHES,
  closeBulkDeleteBranchesDialog,
  clearSelectedBranches,
  onToggleBulkDeleteSelectionMode,
} from '../actions';
import { BranchingModelState } from '../reducers/branch-list-reducer';
import {
  getSelectedBranches,
  getAllBranches,
  getBranchingModel,
} from '../selectors';
import { shouldAllowBulkDelete } from '../utils';

import { reloadBranchesSaga } from './reload-branches-saga';

export function* toggleSelectAllSaga({
  payload: selectAllEnabled,
}: Action<boolean>) {
  const branches: DetailedBranch[] = yield select(getAllBranches);
  const branchingModel: BranchingModelState = yield select(getBranchingModel);
  let selectedBranches: DetailedBranch[] = yield select(getSelectedBranches);
  // Copy them so that we don't end up mutating the state.
  selectedBranches = [...selectedBranches];

  branches.forEach((branch: DetailedBranch) => {
    const selectedIndex = selectedBranches.findIndex(
      (br: DetailedBranch) => br.name === branch.name
    );
    const isSelected = selectedIndex >= 0;

    if (selectAllEnabled && !isSelected) {
      if (shouldAllowBulkDelete(branch, branchingModel)) {
        selectedBranches.push(branch);
      }
    } else if (!selectAllEnabled && isSelected) {
      if (isSelected) {
        selectedBranches.splice(selectedIndex, 1);
      }
    }
  });

  yield put(onToggleBranchSelectSave(selectedBranches));
}

export function* toggleSelectOneSaga({
  payload: branch,
}: Action<DetailedBranch>) {
  let selectedBranches: DetailedBranch[] = yield select(getSelectedBranches);

  if (selectedBranches.find((br: DetailedBranch) => br.name === branch!.name)) {
    selectedBranches = selectedBranches.filter(
      (br: DetailedBranch) => br.name !== branch!.name
    );
  } else {
    selectedBranches = [...selectedBranches, branch!];
  }

  yield put(onToggleBranchSelectSave(selectedBranches));
}

export function* bulkDeleteBranchesSaga() {
  const selectedBranches: DetailedBranch[] = yield select(getSelectedBranches);
  const fullSlug: string = yield select(getCurrentRepositoryFullSlug);
  const url = urls.api.internal.bulkDeleteBranches(fullSlug);
  const request = authRequest(url, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({
      names: selectedBranches.map(br => br.name),
    }),
  });

  try {
    const response: Response = yield call(fetch, request);
    if (response.ok) {
      yield put({
        type: BULK_DELETE_BRANCHES.SUCCESS,
      });

      publishTrackEvent({
        action: 'deleted',
        actionSubject: 'branches',
        actionSubjectId: 'deleteMultipleBranches',
        source: 'branchesScreen',
        attributes: {
          count: selectedBranches.length,
        },
      });
    } else {
      yield captureMessageForResponse(
        response,
        'Bulk deleting branches failed'
      );

      yield put({
        type: BULK_DELETE_BRANCHES.ERROR,
      });
    }
  } catch (error) {
    captureExceptionWithTags(error);
    yield put({
      type: BULK_DELETE_BRANCHES.ERROR,
    });
  }
}

export function* handleBulkDeleteBranchesSuccessSaga() {
  yield put(closeBulkDeleteBranchesDialog());
  yield put(clearSelectedBranches());
  yield put(onToggleBulkDeleteSelectionMode(false));
  yield* reloadBranchesSaga();
  yield put(
    showFlag({
      title: { msg: messages.deleteSuccessTitle },
      description: {
        msg: messages.deleteSuccessDescription,
      },
      id: 'bulk-delete-branches-success',
      iconType: 'success',
      autoDismiss: true,
    })
  );
}

export function* handleBulkDeleteBranchesErrorSaga() {
  yield put(closeBulkDeleteBranchesDialog());
  yield put(
    showFlag({
      title: { msg: messages.deleteFailedTitle },
      description: {
        msg: messages.deleteFailedDescription,
      },
      id: 'bulk-delete-branches-error',
      iconType: 'error',
      autoDismiss: true,
    })
  );
}
